import { party, partyColor, partyLabel, PartyMeta } from "@/lib/parties";
import { SeatTally } from "@/lib/data";

export function PartyBadge({ code, sub }: { code: string; sub?: string }) {
  const p = party(code);
  const label = partyLabel(code, sub);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-xs font-bold"
      style={{ background: p.color, color: p.text }}
    >
      {label.includes(" · ") ? (
        <>
          {label.split(" · ")[0]}
          <span className="font-medium opacity-80">· {label.split(" · ")[1]}</span>
        </>
      ) : (
        label
      )}
    </span>
  );
}

export function PartyDot({ code, className = "" }: { code: string; className?: string }) {
  return (
    <span
      className={`inline-block h-3 w-3 rounded-sm ${className}`}
      style={{ background: partyColor(code) }}
    />
  );
}

// Vote-share margin bars — the recurring visual motif.
export function ShareBars({
  rows,
}: {
  rows: { party: string; sub?: string; name: string; pct: number; votes: number }[];
}) {
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => (
        <div key={i}>
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="truncate font-medium">
              {r.name}{" "}
              <span className="text-muted-foreground">
                ({partyLabel(r.party, r.sub)})
              </span>
            </span>
            <span className="tnum shrink-0 font-semibold">{r.pct.toFixed(1)}%</span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <div className="h-2.5 flex-1 overflow-hidden rounded-sm bg-muted">
              <div
                className="h-full rounded-sm transition-all duration-500"
                style={{
                  width: `${Math.max(r.pct, 1.2)}%`,
                  background: partyColor(r.party),
                }}
              />
            </div>
            <span className="tnum w-16 shrink-0 text-right text-xs text-muted-foreground">
              {r.votes.toLocaleString()}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Horizontal seat-balance composition bar (hemicycle alternative).
export function CompositionBar({
  tally,
  total,
}: {
  tally: SeatTally[];
  total: number;
}) {
  return (
    <div>
      <div className="flex h-7 w-full overflow-hidden rounded-sm border border-border">
        {tally.map((t) => (
          <div
            key={t.party}
            title={`${party(t.party).full}: ${t.seats}`}
            className="flex items-center justify-center text-[11px] font-bold text-white transition-all"
            style={{
              width: `${(t.seats / total) * 100}%`,
              background: partyColor(t.party),
            }}
          >
            {t.seats / total > 0.07 ? t.seats : ""}
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {tally.map((t) => (
          <div key={t.party} className="flex items-center gap-1.5 text-xs">
            <PartyDot code={t.party} />
            <span className="font-semibold">{party(t.party).name}</span>
            <span className="tnum text-muted-foreground">{t.seats}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MajorityLabel({ p }: { p: PartyMeta }) {
  return <span style={{ color: p.color }}>{p.name}</span>;
}
